/**
 * The session.
 *
 * ═══════════════════════════════════════════════════════════════════
 * THIS IS NOT A SECURITY BOUNDARY.
 *
 * `can('ap.invoice.approve')` decides whether to draw a button. It does not
 * decide whether the action succeeds. Every request is authorised on the
 * server, from the session token, against permissions read fresh from the
 * database — so removing a `disabled` attribute in the browser, replaying a
 * request, or calling the API directly gains nothing at all.
 *
 * Hiding controls a user cannot use is a courtesy to them, not a control on
 * them. Both statements need to be true, and both are.
 * ═══════════════════════════════════════════════════════════════════
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError } from './api';
import { useTenant } from './tenant';

export interface EntityAccess {
  entityId: string;
  entityCode: string;
  entityName: string;
  organisationId: string;
  organisationName: string;
  isDemonstration: boolean;
  baseCurrency: string;
}

export interface SessionUser {
  id: string;
  username: string;
  fullName: string;
  email: string;
  jobTitle: string | null;
  entityId: string;
  entities: EntityAccess[];
  roles: Array<{ code: string; name: string; entityId: string | null }>;
  permissions: string[];
}

interface SessionState {
  user: SessionUser | null;
  loading: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  switchEntity: (entityId: string) => Promise<void>;
  refresh: () => Promise<void>;
  can: (permission: string) => boolean;
  canAny: (...permissions: string[]) => boolean;
  /** The entity the user is currently working in. */
  activeEntity: EntityAccess | null;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const { loadConfiguration, clearConfiguration } = useTenant();

  const adopt = useCallback(
    async (next: SessionUser) => {
      setUser(next);
      // The configuration follows the session, because it decides which
      // navigation the shell may draw.
      await loadConfiguration().catch(() => undefined);
    },
    [loadConfiguration],
  );

  const refresh = useCallback(async () => {
    try {
      const response = await api.get<{ user: SessionUser }>('/auth/me');
      await adopt(response.user);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'UNAUTHENTICATED') {
        setUser(null);
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, [adopt]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (username: string, password: string) => {
      const response = await api.post<{ user: SessionUser }>('/auth/login', { username, password });
      await adopt(response.user);
    },
    [adopt],
  );

  const signOut = useCallback(async () => {
    await api.post('/auth/logout').catch(() => undefined);
    setUser(null);
    clearConfiguration();
  }, [clearConfiguration]);

  const switchEntity = useCallback(
    async (entityId: string) => {
      const response = await api.post<{ user: SessionUser }>('/auth/switch-entity', { entityId });
      // Permissions are recomputed for the entity being worked in, so a user
      // may be able to do less here than they could a moment ago. That is the
      // point of entity-scoped roles.
      setUser(response.user);
    },
    [],
  );

  const value = useMemo<SessionState>(() => {
    const held = new Set(user?.permissions ?? []);
    return {
      user,
      loading,
      signIn,
      signOut,
      switchEntity,
      refresh,
      can: (permission: string) => held.has(permission),
      canAny: (...permissions: string[]) => permissions.some((p) => held.has(p)),
      activeEntity: user?.entities.find((e) => e.entityId === user.entityId) ?? null,
    };
  }, [user, loading, signIn, signOut, switchEntity, refresh]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside a SessionProvider.');
  return value;
}
